import type { JsonValue } from '@erikdakoda/database/types';

import { METRIC_VALUE_SOURCE_HEALTHKIT, METRIC_VALUE_SOURCE_HEALTH_CONNECT } from './metricSources';

/** Capgo / Health Connect query strategy for phone sync. */
export type CapgoQueryStrategy = 'aggregated' | 'discrete';

/** Statistic applied to a day's Capgo discrete samples before ingest. */
export type CapgoValueAggregate = 'average' | 'min' | 'max' | 'last';

/** How multiple Oura documents for one day combine into a single value. */
export type OuraDayAggregate = 'sum' | 'min' | 'max' | 'average' | 'last';

/** Blood-pressure field extracted from a Capgo HealthSample. */
export type CapgoValueField = 'systolic' | 'diastolic';

/**
 * Typed view of Metric.sourceInfo for phone and cloud sync.
 * Persisted as Json; parse with {@link parseMetricSourceInfo}.
 */
export type MetricSourceInfoBag = {
  /** Capgo `HealthDataType` string used for HealthKit and Health Connect reads. */
  capgoDataType?: string;
  /** Health Connect aggregation vs discrete samples. */
  healthConnectQuery?: CapgoQueryStrategy;
  /** Legacy HealthKit sample type identifier. */
  healthKitSampleType?: string;
  /** Legacy HealthKit native unit string. */
  healthKitUnit?: string;
  /** HealthKit query strategy from the library seed. */
  healthKitQuery?: 'sum' | 'sum-time' | 'discrete';
  healthKitValueField?: string;
  healthKitFilterValues?: readonly number[];
  healthKitSubtractHours?: number;
  healthConnectSubtractHours?: number;

  /**
   * Ingest reduces same-day samples with this statistic (last / min / max / average).
   * Foreground mapper also emits one reduced row per day when this is set.
   * Absent defaults to last at ingest.
   */
  capgoValueAggregate?: CapgoValueAggregate;
  /** Blood pressure only — read systolic/diastolic off the Capgo HealthSample. */
  capgoValueField?: CapgoValueField;
  /** Oura API endpoint slug. */
  ouraEndpoint?: string;
  /** Field on the Oura day payload (flat). */
  ouraField?: string;
  /** Dot-path into the Oura document (e.g. `spo2_percentage.average`); supersedes flat ouraField. */
  ouraFieldPath?: string;

  /**
   * How multiple documents for one Oura day combine.
   * Sleep periods sum durations; lowest_heart_rate takes min; heart rate series bucket to
   * avg/min/max; default last.
   */
  ouraDayAggregate?: OuraDayAggregate;

  /**
   * Unit slug the provider reports, in Uvilo system-unit vocabulary.
   * Sync ingest converts from this unit into the metric's canonical unit.
   */
  uviloUnit?: string;
};

/** MetricValue.source values that use Metric.sourceInfo for sync mapping. */
export type SyncProviderSource =
  | typeof METRIC_VALUE_SOURCE_HEALTHKIT
  | typeof METRIC_VALUE_SOURCE_HEALTH_CONNECT
  | 'oura';

/**
 * Narrows unknown JSON into a MetricSourceInfoBag when the value is a plain object.
 * @param value Raw Metric.sourceInfo JSON
 * @returns Parsed bag, or null when missing / not an object
 */
export function parseMetricSourceInfo(
  value: JsonValue | null | undefined,
): MetricSourceInfoBag | null {
  if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, JsonValue>;
  const bag: MetricSourceInfoBag = {};

  if (typeof record.capgoDataType === 'string') {
    bag.capgoDataType = record.capgoDataType;
  }
  if (record.healthConnectQuery === 'aggregated' || record.healthConnectQuery === 'discrete') {
    bag.healthConnectQuery = record.healthConnectQuery;
  }
  if (typeof record.healthKitSampleType === 'string') {
    bag.healthKitSampleType = record.healthKitSampleType;
  }
  if (typeof record.healthKitUnit === 'string') {
    bag.healthKitUnit = record.healthKitUnit;
  }
  if (
    record.healthKitQuery === 'sum' ||
    record.healthKitQuery === 'sum-time' ||
    record.healthKitQuery === 'discrete'
  ) {
    bag.healthKitQuery = record.healthKitQuery;
  }
  if (typeof record.healthKitValueField === 'string') {
    bag.healthKitValueField = record.healthKitValueField;
  }
  if (Array.isArray(record.healthKitFilterValues)) {
    bag.healthKitFilterValues = record.healthKitFilterValues.filter(
      (entry): entry is number => typeof entry === 'number',
    );
  }
  if (typeof record.healthKitSubtractHours === 'number') {
    bag.healthKitSubtractHours = record.healthKitSubtractHours;
  }
  if (typeof record.healthConnectSubtractHours === 'number') {
    bag.healthConnectSubtractHours = record.healthConnectSubtractHours;
  }
  if (typeof record.ouraEndpoint === 'string') {
    bag.ouraEndpoint = record.ouraEndpoint;
  }
  if (typeof record.ouraField === 'string') {
    bag.ouraField = record.ouraField;
  }
  if (typeof record.ouraFieldPath === 'string') {
    bag.ouraFieldPath = record.ouraFieldPath;
  }
  if (
    record.ouraDayAggregate === 'sum' ||
    record.ouraDayAggregate === 'min' ||
    record.ouraDayAggregate === 'max' ||
    record.ouraDayAggregate === 'average' ||
    record.ouraDayAggregate === 'last'
  ) {
    bag.ouraDayAggregate = record.ouraDayAggregate;
  }
  if (
    record.capgoValueAggregate === 'average' ||
    record.capgoValueAggregate === 'min' ||
    record.capgoValueAggregate === 'max' ||
    record.capgoValueAggregate === 'last'
  ) {
    bag.capgoValueAggregate = record.capgoValueAggregate;
  }
  if (record.capgoValueField === 'systolic' || record.capgoValueField === 'diastolic') {
    bag.capgoValueField = record.capgoValueField;
  }
  if (typeof record.uviloUnit === 'string') {
    bag.uviloUnit = record.uviloUnit;
  }

  return bag;
}

/** Per-source predicate for whether sourceInfo carries a usable sync mapping. */
const PROVIDER_SOURCE_MAPPING_PREDICATES: Readonly<
  Record<SyncProviderSource, (sourceInfo: MetricSourceInfoBag) => boolean>
> = {
  [METRIC_VALUE_SOURCE_HEALTHKIT]: (sourceInfo) =>
    typeof sourceInfo.capgoDataType === 'string' &&
    (typeof sourceInfo.healthKitSampleType === 'string' ||
      sourceInfo.healthKitQuery === 'sum' ||
      sourceInfo.healthKitQuery === 'sum-time' ||
      sourceInfo.healthKitQuery === 'discrete'),
  [METRIC_VALUE_SOURCE_HEALTH_CONNECT]: (sourceInfo) =>
    typeof sourceInfo.capgoDataType === 'string' &&
    (sourceInfo.healthConnectQuery === 'aggregated' ||
      sourceInfo.healthConnectQuery === 'discrete'),
  oura: (sourceInfo) =>
    typeof sourceInfo.ouraEndpoint === 'string' &&
    (typeof sourceInfo.ouraField === 'string' || typeof sourceInfo.ouraFieldPath === 'string'),
};

/**
 * Returns true when sourceInfo carries a mapping for the given MetricValue.source.
 * @param sourceInfo Parsed sourceInfo bag
 * @param source MetricValue.source string from a sync batch
 */
export function hasProviderSourceMapping(
  sourceInfo: MetricSourceInfoBag | null,
  source: string,
): boolean {
  if (!sourceInfo) {
    return false;
  }

  const predicate = PROVIDER_SOURCE_MAPPING_PREDICATES[source as SyncProviderSource];
  return predicate ? predicate(sourceInfo) : false;
}

/** Per-source accessor for the hours the local-day boundary shifts (sleep-style metrics). */
const PROVIDER_SOURCE_SUBTRACT_HOURS: Readonly<
  Record<SyncProviderSource, (sourceInfo: MetricSourceInfoBag) => number>
> = {
  [METRIC_VALUE_SOURCE_HEALTHKIT]: (sourceInfo) => sourceInfo.healthKitSubtractHours ?? 0,
  [METRIC_VALUE_SOURCE_HEALTH_CONNECT]: (sourceInfo) => sourceInfo.healthConnectSubtractHours ?? 0,
  oura: () => 0,
};

/**
 * Returns how many hours a sample's local day is shifted back for the given provider.
 * @param sourceInfo Parsed sourceInfo bag
 * @param source MetricValue.source identity for the sync provider
 * @returns Hours to subtract before bucketing a sample into a local day; 0 when unmapped
 */
export function getProviderSubtractHours(
  sourceInfo: MetricSourceInfoBag | null,
  source: string,
): number {
  if (!sourceInfo) {
    return 0;
  }

  const accessor = PROVIDER_SOURCE_SUBTRACT_HOURS[source as SyncProviderSource];
  return accessor ? accessor(sourceInfo) : 0;
}

/**
 * Capgo `queryAggregated` rejects these data types on iOS (quantity/category limits).
 * Foreground sync reads them via `readSamples` and day-sums client-side instead.
 */
const CAPGO_QUERY_AGGREGATED_UNSUPPORTED = new Set([
  'appleStandHour',
  'exerciseTime',
  'flightsClimbed',
  'mindfulness',
  'sleep',
]);

/**
 * Capgo cannot read these data types on iOS (category vs quantity); skip foreground sync.
 */
const CAPGO_READ_UNSUPPORTED = new Set(['appleStandHour', 'mindfulness']);

/**
 * Resolves the Capgo read plan for a metric (data type + aggregated vs discrete).
 * Forces discrete for Capgo types that reject `queryAggregated`.
 * @param sourceInfo Parsed sourceInfo bag
 * @returns Capgo plan, or null when the metric is not Capgo-readable
 */
export function getCapgoReadPlan(
  sourceInfo: MetricSourceInfoBag | null,
): { dataType: string; strategy: CapgoQueryStrategy } | null {
  if (!sourceInfo?.capgoDataType) {
    return null;
  }

  if (CAPGO_READ_UNSUPPORTED.has(sourceInfo.capgoDataType)) {
    return null;
  }

  let strategy: CapgoQueryStrategy;

  if (
    sourceInfo.healthConnectQuery === 'aggregated' ||
    sourceInfo.healthConnectQuery === 'discrete'
  ) {
    strategy = sourceInfo.healthConnectQuery;
  } else if (sourceInfo.healthKitQuery === 'discrete') {
    strategy = 'discrete';
  } else if (sourceInfo.healthKitQuery === 'sum' || sourceInfo.healthKitQuery === 'sum-time') {
    strategy = 'aggregated';
  } else {
    strategy = 'aggregated';
  }

  if (
    strategy === 'aggregated' &&
    CAPGO_QUERY_AGGREGATED_UNSUPPORTED.has(sourceInfo.capgoDataType)
  ) {
    strategy = 'discrete';
  }

  return { dataType: sourceInfo.capgoDataType, strategy };
}

/**
 * True when sourceInfo asks Capgo for a cumulative day total (sum / sum-time / HC aggregated).
 * Used to day-sum discrete samples when Capgo cannot `queryAggregated`.
 * @param sourceInfo Parsed sourceInfo bag
 */
export function wantsCapgoDaySum(sourceInfo: MetricSourceInfoBag | null): boolean {
  if (!sourceInfo) {
    return false;
  }
  return (
    sourceInfo.healthKitQuery === 'sum' ||
    sourceInfo.healthKitQuery === 'sum-time' ||
    sourceInfo.healthConnectQuery === 'aggregated'
  );
}

/**
 * Returns the provider-reported unit slug from sourceInfo, or null when missing.
 * @param sourceInfo Parsed sourceInfo bag
 */
export function getProviderUnitSlug(sourceInfo: MetricSourceInfoBag | null): string | null {
  if (!sourceInfo?.uviloUnit) {
    return null;
  }
  return sourceInfo.uviloUnit;
}
